import { Container, Rectangle, type FederatedPointerEvent, type Ticker } from 'pixi.js';
import { isLethal, type CardDef } from '../../core/card';
import { cardLore } from '../../core/cardText';
import type { CardUid } from '../../core/ids';
import { pointInPoly, type Pt } from '../card/art/geom';
import { canHoverNow } from '../input/current';
import { CardView } from '../card/cardView';
import type { CardFaceCache } from '../card/faceCache';
import { faceSize, type FaceSize } from '../card/metrics';
import { DEFAULT_POSE, type CardPose } from '../card/pose';
import { CardRig, MAX_STEP_SECONDS } from '../hand/cardRig';
import type { FeelParams } from '../hand/feel';
import { DYING_GROW_TO, FADE_MS, REVEAL_CARD_HEIGHT } from './drawReveal';

/**
 * **浮在画面正中的那一张牌**（造成濒死的那张异常牌）。
 *
 * 评审：
 *
 * > 我无法 hover 造成濒死效果的、浮在屏幕中央的牌，以查看它的 lore。
 * > 因为正常情况下异常牌视觉上不会进入手牌，hover 屏幕中央的濒死牌是唯一
 * > 查看其 lore 的方式。应当使得浮在屏幕中央的牌有正常手牌的一切属性和功能，
 * > 例如自然摆动，随音乐鼓动，hover 有 lore 之类。**不要重复实现功能**，
 * > 或许你应该将手牌的部分实现抽象出来，并令其也适用于屏幕中央的牌。
 *
 * 所以"这张牌怎么动"用的是**和手牌同一个** `CardRig`（`hand/cardRig.ts`）：
 * 空闲摇摆、hover 抬起与放大、按下、音乐脉动、`chase`/`approach` 的平滑，
 * 一行都没有重写 —— 这里只补三件手牌那边由**扇形**提供、而这里没有的东西：
 *
 * | 手牌那边 | 这里 |
 * | --- | --- |
 * | `handLayout()` 算出来的槽位 | 一个固定姿态：画面正中、`scale = 1` |
 * | 只看 y 的横带命中测试 | **逐张的多边形命中测试**（它就悬在画面正中，用 y 判中会乱套） |
 * | 整扇上移 / 邻居避让的两个包络 | 恒为 0（只有一张牌，没有扇、没有邻居） |
 *
 * ## ⚠️ 牌面按**它自己的最终大小**烘
 *
 * 这一张一直是"2 倍牌高"（见 `DrawReveal.DYING_GROW_TO`），所以调用方传进来的
 * `height` 就是它在屏幕上的高度，`CardView` 也按它建 —— 于是牌面（包括字）
 * 天然就是清晰的，不需要超采样那一套。手牌那边同理（132 就按 132 烘）。
 *
 * ## 它和 `DrawReveal` 的关系
 *
 * ```
 *   DrawReveal（翻牌那一段）  飞到中点 → 先缩后撑到 2 倍 → 停住
 *        │  停住（`holding`）那一帧换手
 *        ▼
 *   CenteredCard（就是本文件）  自然摆动 · 随音乐鼓动 · hover 看 lore · 等濒死结束
 * ```
 *
 * 换手是**无缝的**：交接那一刻两边都是"画面正中、2 倍大小、正面朝外"。
 * 之后 `release()` 只负责淡出（濒死一结束，这张牌的去向由状态决定：
 * 拆弹回牌堆 / 爆炸进弃牌堆，都不该由这张牌自己决定 —— 见 `drawReveal.ts`）。
 */

/** 一张牌的包围盒（画布逻辑坐标）。形状和 `hand` 那边的 `boxOf` 一致 */
export interface CardBox {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

export interface CenteredCardOptions {
  /** 在哪收指针事件。和手牌用的是同一个（一般就是 `app.stage`） */
  readonly input: Container;
  readonly faces: CardFaceCache;
  readonly def: CardDef;
  /**
   * **牌高（逻辑像素）**。给的是它在屏幕上**最终**的高度。
   *
   * ⚠️ 牌面按它烘，所以这里不要传"1 倍牌高再靠 `scale` 放大" —— 那样字会糊
   *    （这正是评审报过的另一个问题）。
   */
  readonly height: number;
  /** 牌心在哪（画布逻辑坐标） */
  readonly at: Pt;
  /**
   * 手感参数。不给就是手牌那一套默认值。
   *
   * ⚠️ 调用方（对局屏）会**压低 hover 那几项**：这一张身后压着濒死层预烘的
   *    辉光/阴影，抬得太高会和自己的光晕脱开。见 `ui/screens/game.ts` 的
   *    `spawnCentered`。
   */
  readonly feel?: Partial<FeelParams>;
  /** 淡出时长（毫秒）。默认就是 `DrawReveal` 那一条（换手前后"走"的观感一致） */
  readonly fadeMs?: number;
}

/**
 * **濒死时的内置 lore**：底包的异常牌 lore 不再自带这句（它在手牌 / 记牌器
 * 里也能看到，那些场合说"现下无人为你打出[捕获]"并不对）。只有**屏幕中央
 * 这张致命牌**在濒死期间才把它接到 lore 末尾。
 */
export const DYING_LETHAL_LORE = 'render.dyingLethalLore' as const;

export class CenteredCard {
  /** 加进场景的东西。**要加在牌那一层之上**（它是最显眼的那一张） */
  readonly view: Container;

  readonly card: CardView;
  readonly def: CardDef;

  readonly #rig: CardRig;
  readonly #input: Container;
  readonly #size: FaceSize;
  readonly #fadeMs: number;

  /**
   * **它的槽位** —— 画面正中、大小 1（相对"它自己的牌高"）。
   *
   * ⚠️ 手牌那边这个点是 `handLayout()` 算出来的；这里是一个常数，但**类型一样**，
   *    于是 `feelTarget` 两边通用（空闲摇摆、hover 抬起都加在它上面）。
   * `perspective` 用默认值：它不歪、不透视，正对镜头。
   */
  readonly #pose: CardPose;

  #pointer: [number, number] | null = null;
  /** 跟着指针走（塞回异常时用）。关掉就用 `#pose` 的固定位置 */
  #follow = false;
  #hovered = false;
  /** 触控点按「钉住」的说明（能 hover 的指针不用它） */
  #pinned = false;
  #released = false;
  #alpha = 1;

  constructor(options: CenteredCardOptions) {
    this.#input = options.input;
    this.#fadeMs = options.fadeMs ?? FADE_MS;
    this.def = options.def;

    // ⚠️ 长宽比只有 `metrics.ts` 说了算，这里不许自己算
    this.#size = faceSize(options.height);
    this.#pose = { ...DEFAULT_POSE, x: options.at.x, y: options.at.y };

    this.view = new Container();
    this.view.label = 'centered-card';
    this.view.sortableChildren = true;
    this.#rig = new CardRig({
      view: this.view,
      ...(options.feel === undefined ? {} : { feel: options.feel }),
    });

    this.card = new CardView({
      texture: options.faces.get(options.def, this.#size),
      size: this.#size,
    });
    // ⚠️ 和手牌一样：命中测试是我们自己做的（四角现成，又便宜又准）
    this.card.eventMode = 'none';
    this.#rig.add(this.card);
    this.#rig.settle(0, this.#pose);

    this.#input.on('globalpointermove', this.#onMove, this);

    // 触控没有 hover：点一下这张牌 = 显示它的 lore（再点一下收起）。
    // 鼠标走 hover，不需要这条。
    this.view.eventMode = 'static';
    this.view.hitArea = new Rectangle(
      this.#pose.x - this.#size.width / 2,
      this.#pose.y - this.#size.height / 2,
      this.#size.width,
      this.#size.height,
    );
    this.view.on('pointertap', () => {
      if (canHoverNow()) return;
      this.#pinned = !this.#pinned;
    });
  }

  // ---- 读数与输入 ----

  /** 现在被 hover 着吗（触控点按钉住的也算） */
  get hovered(): boolean {
    return this.#hovered || this.#pinned;
  }

  /** **hover 到的是哪张牌的定义** —— 悬停说明靠它（形状和 `Hand` 一致） */
  get hoveredDef(): CardDef | null {
    if (!this.hovered) return null;
    // ★ 濒死提示**只**加在屏幕中央这张致命牌的 lore 末尾（用户要求）
    if (!isLethal(this.def)) return this.def;
    const base = cardLore(this.def);
    // 追加成一个 hint 片段（用 i18n 键，切语言时跟着变）；清掉 loreEn，
    // 让 cardLore() 无条件用这份已本地化的合并结构。
    return {
      ...this.def,
      lore: { ...base, hint: [...(base.hint ?? []), { type: 'key', key: DYING_LETHAL_LORE }] },
      loreEn: undefined,
    };
  }

  /** 那张牌在画布上的包围盒（悬停说明定位用）。没 hover 就是 `null` */
  hoveredBox(): CardBox | null {
    if (!this.hovered) return null;
    const c = this.card.corners;
    const xs = [c.x0, c.x1, c.x2, c.x3];
    const ys = [c.y0, c.y1, c.y2, c.y3];
    const minX = Math.min(...xs) + this.card.position.x;
    const minY = Math.min(...ys) + this.card.position.y;
    const maxX = Math.max(...xs) + this.card.position.x;
    const maxY = Math.max(...ys) + this.card.position.y;
    return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
  }

  /** 音乐敲了一下 → 跟着鼓动（和手牌同一个入口，宿主每小节敲一次） */
  pulse(): void {
    this.#rig.pulse();
  }

  /** 当前脉动值（读数用） */
  get pulseValue(): number {
    return this.#rig.pulseValue;
  }

  /** 空闲摇摆走到哪儿了（秒）。读数用 —— "它还活着吗"看这个数**必须**在涨 */
  get time(): number {
    return this.#rig.time;
  }

  /** 现在的不透明度（读数用） */
  get alpha(): number {
    return this.#alpha;
  }

  /**
   * **濒死结束了**：这一张该走了。
   *
   * ⚠️ 和 `DrawReveal.release()` 一个语义：只负责让它**淡出** ——
   *    拆了弹要回牌堆、炸了要进弃牌堆，两种去向都不该由这张牌自己决定。
   */
  release(): void {
    if (this.#released) return;
    this.#released = true;
  }

  /** 让这张牌跟随指针（塞回异常时用）。见 `#follow` */
  setFollow(on: boolean): void {
    this.#follow = on;
  }

  /**
   * 显示 / 隐藏这一张。
   *
   * 塞回异常时，牌扇里另有一张可拖的 [异常]（用户要求），所以把正中这张
   * 藏起来，免得两张叠在一起。
   */
  setVisible(on: boolean): void {
    this.view.visible = on;
  }

  /** 淡出完了没有。完了由调用方销毁 */
  get finished(): boolean {
    return this.#released && this.#alpha <= 0;
  }

  // ---- 每帧 ----

  /**
   * 推进一帧。挂到**那唯一一个** ticker 回调上（`architecture.md` §7.1）。
   *
   * ⚠️ 手牌那边推 `HandView.update`，这里推 `CenteredCard.update` ——
   *    两者内部调的都是 `CardRig.step`。
   */
  update(ticker: Ticker): void {
    if (this.finished) return;
    const dt = Math.min(ticker.deltaMS / 1000, MAX_STEP_SECONDS);

    // 淡出：`release()` 之后才走，走完由调用方销毁
    if (this.#released) {
      this.#alpha = Math.max(0, this.#alpha - ticker.deltaMS / this.#fadeMs);
      this.view.alpha = this.#alpha;
    }

    // ★ 塞回异常：这张牌跟着指针走（**不需要按住鼠标**），玩家把它拖到牌堆位置。
    //   `#pose` 是只读的，所以跟随态用一份覆盖了 x/y 的副本。
    const posture: CardPose =
      this.#follow && this.#pointer !== null
        ? { ...this.#pose, x: this.#pointer[0], y: this.#pointer[1] }
        : this.#pose;

    this.#rig.step(dt, 0, 0, [
      {
        posture,
        input: {
          // 只有一张牌：相位固定，空闲摇摆照样走它自己的时间轴
          phase: 0,
          index: 0,
          // ⚠️ 它没有"位次"可言 —— 传 -1，于是 `feelTarget` 里
          //    "哪一位被 hover" 那套（只对扇形有意义）不会把它自己算成邻居
          hovered: this.#hovered ? 0 : -1,
          selected: false,
          pointer: this.#pointer,
          unit: this.#size.height,
          wide: this.#size.width,
        },
        wantHover: this.#hovered ? 1 : 0,
        wantPress: 0,
        z: 0,
      },
    ]);
  }

  destroy(): void {
    this.#input.off('globalpointermove', this.#onMove, this);
    this.#rig.clear();
    this.view.destroy({ children: true });
  }

  // ---- 内部 ----

  readonly #onMove = (e: FederatedPointerEvent): void => {
    const p = this.view.toLocal(e.global);
    this.#pointer = [p.x, p.y];
    /**
     * ⚠️ **逐个四角的多边形命中测试**，不是手牌那条"只看 y"的横带规则 ——
     *    它就悬在画面正中，按 y 判中会让整条横线上的点击都算指着它。
     *    四角是现成的（`CardView.corners`），和手牌用的是同一套办法。
     */
    const c = this.card.corners;
    const cx = this.card.position.x;
    const cy = this.card.position.y;
    // 只对能 hover 的指针算：触控点完指针位置会滞留，否则这张牌会一直抬着
    const canHover = canHoverNow();
    if (canHover) this.#pinned = false;
    this.#hovered = canHover && pointInPoly(p, [
      { x: c.x0 + cx, y: c.y0 + cy },
      { x: c.x1 + cx, y: c.y1 + cy },
      { x: c.x2 + cx, y: c.y2 + cy },
      { x: c.x3 + cx, y: c.y3 + cy },
    ]);
  };
}

/**
 * **"停在正中那一张"的管家** —— 热座和联机两个对局屏共用这一份。
 *
 * 两个屏的抽牌动画是各自的一份循环（它们的历史结构如此），但"翻完牌之后
 * 停在正中那一张"从换手到淡出走的**是同一件事** —— 评审要求"不要重复实现功能"，
 * 所以那一段收在这里，两个屏各自只留三行：
 *
 * ```ts
 *   // 每帧
 *   stage.update(ticker, ctx.energy?.isDying === true);
 *   // 翻牌层停稳那一刻
 *   if (!entry.handedOff && entry.view.holding) { entry.handedOff = true; stage.start(entry.uid); entry.view.destroy(); }
 *   // 它走了（淡出完）→ 收尾
 *   if (entry.handedOff && stage.finished) { ...splice / inFlight 记账... }
 * ```
 */
export interface CenteredStageOptions {
  /** 收指针事件的地方（和手牌同一个） */
  readonly input: Container;
  readonly faces: CardFaceCache;
  readonly layer: Container;
  readonly bounds: { readonly width: number; readonly height: number };
  /** uid → 牌定义。查不到就不交接（上层那一套"查不到就跳过"） */
  readonly defOf: (uid: CardUid) => CardDef | undefined;
  /**
   * 牌高（逻辑像素）。默认 `REVEAL_CARD_HEIGHT × DYING_GROW_TO` ——
   * 也就是"停在正中时它在屏幕上的大小"。
   */
  readonly height?: number;
  /** 手感覆盖。见 `CenteredCardOptions.feel` */
  readonly feel?: Partial<FeelParams>;
}

export class CenteredStage {
  #card: CenteredCard | null = null;
  /** 已经淡出完了（上一次交接的那张已经走了）。调用方据此收尾 */
  #finished = false;
  readonly #options: CenteredStageOptions;

  constructor(options: CenteredStageOptions) {
    this.#options = options;
  }

  /** 现在浮着哪一张（没有就是 `null`） */
  /** 让正中这张牌跟随指针（塞回异常时用） */
  setFollow(on: boolean): void {
    this.#card?.setFollow(on);
  }

  /** 显示 / 隐藏正中这张牌（塞回异常时扇里有另一张 [异常]） */
  setVisible(on: boolean): void {
    this.#card?.setVisible(on);
  }

  get card(): CenteredCard | null {
    return this.#card;
  }

  /** 上一张走完了没有。`start()` 会把它清掉 */
  get finished(): boolean {
    return this.#finished;
  }

  /**
   * **把某一 uid 的牌接到正中。** 已经在手上的话是空操作（幂等）。
   *
   * 时机是翻牌层"停稳"那一帧（`DrawReveal.holding`）—— 那一刻两边姿态完全一样，
   * 所以换手是看不出来的。
   */
  start(uid: CardUid): CenteredCard | null {
    if (this.#card !== null) return this.#card;
    const def = this.#options.defOf(uid);
    if (def === undefined) return null;
    const bounds = this.#options.bounds;
    const card = new CenteredCard({
      input: this.#options.input,
      faces: this.#options.faces,
      def,
      height: this.#options.height ?? REVEAL_CARD_HEIGHT * DYING_GROW_TO,
      at: { x: bounds.width / 2, y: bounds.height / 2 },
      ...(this.#options.feel === undefined ? {} : { feel: this.#options.feel }),
    });
    this.#options.layer.addChild(card.view);
    this.#card = card;
    this.#finished = false;
    return card;
  }

  /**
   * 每帧推一次。
   *
   * `dying` = 现在还在不在濒死：**一结束就开始淡出**（评审："停留在屏幕中央"
   * 是一个状态、不是终点），淡完销毁并置 `finished`。
   */
  update(ticker: Ticker, dying: boolean): void {
    const card = this.#card;
    if (card === null) return;
    if (!dying) card.release();
    card.update(ticker);
    if (!card.finished) return;
    card.destroy();
    this.#card = null;
    this.#finished = true;
  }

  /** 音乐敲了一下 → 浮着的那张跟着鼓动（和手牌同一个入口） */
  pulse(): void {
    this.#card?.pulse();
  }

  /** 悬停说明要问的那一半：现在被指着的是哪张牌（没有就是 `null`） */
  hovered(): CenteredCard | null {
    const card = this.#card;
    return card !== null && card.hoveredDef !== null ? card : null;
  }

  destroy(): void {
    this.#card?.destroy();
    this.#card = null;
  }
}
